/** @jsxImportSource solid-js */

import * as CDM from 'trimble-connect-sdk';
import {
  Accessor,
  Component,
  createContext,
  createSignal,
  JSX,
  useContext,
} from 'solid-js';
import { mapServiceRegionToRegion } from './utils';

export interface ProjectRegionData {
  readonly regions: CDM.Server[];
  readonly region: string;
  readonly isLoading: boolean;
  readonly project: CDM.Project | null;
  readonly error: string | null;
}

interface ProjectRegionService {
  readonly data: Accessor<ProjectRegionData>;
  fetchRegionsAndProject(
    projectId: string,
    accessToken: string,
    tcpsApiDomain: string
  ): Promise<void>;
  mapServiceRegionToRegion(location: string): string;
}

const ProjectRegionContext = createContext<ProjectRegionService>();

export interface ProjectRegionProviderProps {
  readonly children?: JSX.Element;
}

export const ProjectRegionProvider: Component<ProjectRegionProviderProps> = (
  props: ProjectRegionProviderProps
) => {
  const [regions, setRegions] = createSignal<CDM.Server[]>([]);
  const [region, setRegion] = createSignal<string>('');
  const [isLoading, setIsLoading] = createSignal(false);
  const [project, setProject] = createSignal<CDM.Project | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [hasInitialized, setHasInitialized] = createSignal(false);

  // Function to fetch regions and determine region for the project
  const fetchRegionsAndProject = async (
    projectId: string,
    accessToken: string,
    tcpsApiDomain: string
  ): Promise<void> => {
    // Prevent multiple simultaneous calls for the same project
    const currentProject = project();
    if (hasInitialized() && currentProject?.id === projectId) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setHasInitialized(true);

      // Configure TCPSClient with environment-specific domain (initial call before we know region)
      CDM.TCPSClient.config.serviceUri = tcpsApiDomain + '/';

      // First, get all available regions
      const regionsResponse = await CDM.TCPSClient.listServers();

      CDM.TCPSClient.config.credentials = { token: accessToken };

      const serverRegions = regionsResponse.data;
      setRegions(serverRegions);

      let projectData: CDM.Project | null = null;

      try {
        const projectResponse = await CDM.TCPSClient.getProject(projectId);
        projectData = projectResponse.data;
      } catch {
        // If that fails, try each region individually
        for (const server of serverRegions) {
          try {
            const projectResponse = await CDM.TCPSClient.getProject(
              projectId,
              server
            );
            projectData = projectResponse.data;
            break;
          } catch {
            continue;
          }
        }
      }

      if (projectData) {
        setProject(projectData);

        if (projectData.location) {
          const mappedRegion = mapServiceRegionToRegion(projectData.location);
          setRegion(mappedRegion);
        } else {
          console.warn('Could not determine region for project', projectId);
        }
      } else {
        console.error('Could not fetch project from any region');
        setError('Could not fetch project from any region');
      }
    } catch (error) {
      console.error('Error fetching regions or project:', error);
      setError(
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
      // Reset initialization flag on error to allow retry
      setHasInitialized(false);
    } finally {
      setIsLoading(false);
    }
  };

  const service: ProjectRegionService = {
    data: () => ({
      regions: regions(),
      region: region(),
      isLoading: isLoading(),
      project: project(),
      error: error(),
    }),
    fetchRegionsAndProject,
    mapServiceRegionToRegion,
  };

  return (
    <ProjectRegionContext.Provider value={service}>
      {props.children}
    </ProjectRegionContext.Provider>
  );
};

export const useProjectRegion = (): ProjectRegionService => {
  const context = useContext(ProjectRegionContext);
  if (context === undefined) {
    throw new Error(
      'useProjectRegion must be used within a ProjectRegionProvider'
    );
  }

  return context;
};
